import { useMemo, useState, useEffect } from "react";
import { useCrm } from "@/lib/crm";
import { useAuth } from "@/lib/auth";
import { eur, eur2, dateIt, MOVIMENTO_AREE, areaLabel, portfolioRicavi, MESI, compensoSsd, portfolioIncassato, isAcq } from "@/lib/constants";
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, Legend } from "recharts";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Lock, TrendingUp, TrendingDown, Wallet, Briefcase, Users, Layers, Plus, Trash2, Pencil, ShieldCheck, KeyRound } from "lucide-react";

const emptyMov = { data: "", descrizione: "", categoria: "Generale", importo: 0, tipo: "costo", area: "generale" };

const monthLabel = (ym) => {
  const [y, m] = ym.split("-");
  return `${MESI[+m - 1].slice(0, 3)} ${y.slice(2)}`;
};

export default function Controllo() {
  const { settings, api } = useCrm();
  const { isTitolare } = useAuth();
  const [ok, setOk] = useState(() => sessionStorage.getItem("efp-controllo") === "1");
  const [pin, setPin] = useState("");
  const [checking, setChecking] = useState(false);

  if (!isTitolare) {
    return (
      <div className="max-w-sm mx-auto mt-16 animate-fade-up" data-testid="controllo-denied">
        <Card className="p-8 text-center">
          <div className="h-14 w-14 rounded-2xl bg-red-100 dark:bg-red-500/15 flex items-center justify-center mx-auto mb-4"><Lock className="h-7 w-7 text-red-500" /></div>
          <h1 className="text-xl font-extrabold text-slate-900 dark:text-white">Area riservata</h1>
          <p className="text-sm text-slate-500 mt-1">Questa sezione è accessibile solo al titolare.</p>
        </Card>
      </div>
    );
  }

  const verify = async () => {
    setChecking(true);
    try {
      const res = await api.controlloVerify(pin);
      if (res.ok) {
        sessionStorage.setItem("efp-controllo", "1");
        setOk(true);
      } else {
        toast.error("PIN errato");
      }
    } finally {
      setChecking(false);
    }
  };

  if (!ok) {
    return (
      <div className="max-w-sm mx-auto mt-16 animate-fade-up">
        <Card className="p-8 text-center">
          <div className="h-14 w-14 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
            <Lock className="h-7 w-7 text-primary" />
          </div>
          <h1 className="text-2xl font-extrabold text-slate-900 dark:text-white">Area Titolare</h1>
          <p className="text-sm text-slate-500 mt-1 mb-6">Inserisci il PIN per accedere al controllo economico.</p>
          <Input
            data-testid="controllo-pin-input"
            type="password"
            inputMode="numeric"
            value={pin}
            onChange={(e) => setPin(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && verify()}
            placeholder="PIN"
            className="text-center text-lg tracking-widest"
          />
          <Button data-testid="controllo-pin-submit" onClick={verify} disabled={checking || !pin} className="w-full mt-4 rounded-full">
            {checking ? "Verifica…" : "Accedi"}
          </Button>
          <p className="text-[11px] text-slate-400 mt-4">Protezione leggera per account condiviso, non è una password di sicurezza.</p>
        </Card>
      </div>
    );
  }

  return <ControlloContent />;
}

function ControlloContent() {
  const { ssd, collaboratori, portfolio, settings, movimenti, api, refresh, setSettings } = useCrm();
  const comm = settings?.commissione ?? 0.5;
  const collabMap = useMemo(() => Object.fromEntries(collaboratori.map((c) => [c.id, c])), [collaboratori]);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyMov);
  const [newPin, setNewPin] = useState("");

  const data = useMemo(() => {
    // Portfolio
    const pRicavi = portfolio.reduce((a, p) => a + portfolioRicavi(p), 0);
    const pCosti = portfolio.reduce((a, p) => a + (Number(p.costi) || 0), 0);

    // Collaboratori (pipeline acquisita): ricavo = incasso azienda; compenso 50% = costo
    const acquired = ssd.filter((s) => isAcq(s, settings));
    const cRicavi = acquired.reduce((a, s) => a + (s.ricavo || 0), 0);
    const cCompensi = acquired.reduce((a, s) => a + compensoSsd(s, collabMap[s.collaboratore_id], comm, settings), 0);

    // Movimenti manuali per area
    const manual = { portfolio: { r: 0, c: 0 }, collaboratori: { r: 0, c: 0 }, generale: { r: 0, c: 0 } };
    movimenti.forEach((m) => {
      const bucket = manual[m.area] || manual.generale;
      if (m.tipo === "ricavo") bucket.r += Number(m.importo) || 0;
      else bucket.c += Number(m.importo) || 0;
    });

    const areas = {
      portfolio: { ricavi: pRicavi + manual.portfolio.r, costi: pCosti + manual.portfolio.c },
      collaboratori: { ricavi: cRicavi + manual.collaboratori.r, costi: cCompensi + manual.collaboratori.c },
      generale: { ricavi: manual.generale.r, costi: manual.generale.c },
    };
    Object.values(areas).forEach((x) => (x.margine = x.ricavi - x.costi));

    const totRicavi = areas.portfolio.ricavi + areas.collaboratori.ricavi + areas.generale.ricavi;
    const totCosti = areas.portfolio.costi + areas.collaboratori.costi + areas.generale.costi;

    return { areas, totRicavi, totCosti, utile: totRicavi - totCosti, cCompensi };
  }, [ssd, portfolio, movimenti, comm, collabMap, settings]);

  const chartData = useMemo(() => {
    const map = {};
    const bump = (ym, field, val) => {
      if (!ym) return;
      if (!map[ym]) map[ym] = { ym, ricavi: 0, costi: 0 };
      map[ym][field] += val;
    };
    movimenti.forEach((m) => bump((m.data || "").slice(0, 7), m.tipo === "ricavo" ? "ricavi" : "costi", Number(m.importo) || 0));
    ssd.filter((s) => isAcq(s, settings) && s.data_acquisizione).forEach((s) => {
      const ym = s.data_acquisizione.slice(0, 7);
      bump(ym, "ricavi", s.ricavo || 0);
      bump(ym, "costi", compensoSsd(s, collabMap[s.collaboratore_id], comm, settings));
    });
    return Object.values(map)
      .sort((a, b) => a.ym.localeCompare(b.ym))
      .map((x) => ({ ...x, utile: x.ricavi - x.costi, label: monthLabel(x.ym) }));
  }, [movimenti, ssd, comm, collabMap, settings]);

  const save = async () => {
    if (!form.data || !form.descrizione.trim()) { toast.error("Data e descrizione obbligatorie"); return; }
    const payload = { ...form, importo: Number(form.importo) || 0 };
    if (editing) { await api.updateMovimento(editing.id, payload); toast.success("Movimento aggiornato"); }
    else { await api.createMovimento(payload); toast.success("Movimento aggiunto"); }
    setOpen(false); setEditing(null); setForm(emptyMov); await refresh();
  };
  const del = async (m) => { await api.deleteMovimento(m.id); await refresh(); toast.success("Movimento eliminato"); };

  const savePin = async () => {
    if (!newPin.trim()) return;
    const s = await api.updateSettings({ controllo_pin: newPin });
    setSettings(s);
    setNewPin("");
    toast.success("PIN aggiornato");
  };

  const areaMeta = {
    portfolio: { icon: Briefcase, color: "text-slate-900 dark:text-white", bg: "bg-slate-900 dark:bg-slate-700" },
    collaboratori: { icon: Users, color: "text-sky-600", bg: "bg-sky-500" },
    generale: { icon: Layers, color: "text-purple-600", bg: "bg-purple-500" },
  };

  const sortedMov = [...movimenti].sort((a, b) => (b.data || "").localeCompare(a.data || ""));

  return (
    <div className="space-y-8">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 dark:text-white flex items-center gap-2">
            <ShieldCheck className="h-7 w-7 text-primary" /> Controllo Economico
          </h1>
          <p className="text-slate-500 mt-1">Quadro completo costi e ricavi — differenziato Portfolio e Collaboratori.</p>
        </div>
        <Button data-testid="add-movimento-button" onClick={() => { setEditing(null); setForm(emptyMov); setOpen(true); }} className="rounded-full gap-1.5">
          <Plus className="h-4 w-4" /> Nuova voce
        </Button>
      </div>

      {/* Totali azienda */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card data-testid="controllo-tot-ricavi" className="p-5">
          <div className="flex items-center justify-between"><span className="text-xs uppercase tracking-wider text-slate-400">Ricavi totali</span><TrendingUp className="h-4 w-4 text-emerald-500" /></div>
          <div className="text-3xl font-extrabold mt-2 tabular-nums text-emerald-600">{eur(data.totRicavi)}</div>
        </Card>
        <Card data-testid="controllo-tot-costi" className="p-5">
          <div className="flex items-center justify-between"><span className="text-xs uppercase tracking-wider text-slate-400">Costi totali</span><TrendingDown className="h-4 w-4 text-red-500" /></div>
          <div className="text-3xl font-extrabold mt-2 tabular-nums text-red-500">{eur(data.totCosti)}</div>
        </Card>
        <Card data-testid="controllo-utile" className="p-5 bg-primary/5 border-primary/20">
          <div className="flex items-center justify-between"><span className="text-xs uppercase tracking-wider text-primary/70">Utile azienda</span><Wallet className="h-4 w-4 text-primary" /></div>
          <div className={`text-3xl font-extrabold mt-2 tabular-nums ${data.utile >= 0 ? "text-primary" : "text-red-500"}`}>{eur(data.utile)}</div>
        </Card>
      </div>

      {portfolio.length > 0 && (() => {
        const inc = portfolio.reduce((a, p) => a + portfolioIncassato(p), 0);
        const tot = portfolio.reduce((a, p) => a + portfolioRicavi(p), 0);
        return (
          <Card data-testid="controllo-incassi-portfolio" className="p-5 flex flex-wrap items-center gap-x-8 gap-y-2">
            <span className="font-semibold text-slate-900 dark:text-white inline-flex items-center gap-2"><Briefcase className="h-4 w-4 text-primary" /> Incassi Portfolio</span>
            <span className="text-sm text-slate-500">Lavori: <b className="text-sky-600 tabular-nums">{eur(tot)}</b></span>
            <span className="text-sm text-slate-500">Incassato: <b className="text-emerald-600 tabular-nums">{eur(inc)}</b></span>
            <span className="text-sm text-slate-500">Da incassare: <b className="text-rose-600 tabular-nums">{eur(tot - inc)}</b></span>
          </Card>
        );
      })()}

      {/* Per area */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {MOVIMENTO_AREE.map((a) => {
          const x = data.areas[a.key];
          const meta = areaMeta[a.key];
          return (
            <Card key={a.key} data-testid={`controllo-area-${a.key}`} className="p-5 animate-fade-up">
              <div className="flex items-center gap-2.5 mb-4">
                <span className={`h-9 w-9 rounded-xl flex items-center justify-center ${meta.bg}`}><meta.icon className="h-4.5 w-4.5 text-white" /></span>
                <span className="font-bold text-slate-900 dark:text-white">{a.label}</span>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-slate-500">Ricavi</span><span className="font-semibold text-emerald-600 tabular-nums">{eur2(x.ricavi)}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Costi{a.key === "collaboratori" ? " (compensi + voci)" : ""}</span><span className="font-semibold text-red-500 tabular-nums">{eur2(x.costi)}</span></div>
                <div className="flex justify-between pt-2 border-t border-slate-100 dark:border-slate-800"><span className="font-medium text-slate-700 dark:text-slate-200">Margine</span><span className={`font-bold tabular-nums ${x.margine >= 0 ? "text-slate-900 dark:text-white" : "text-red-500"}`}>{eur2(x.margine)}</span></div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Grafico andamento mensile */}
      <Card className="p-6 animate-fade-up" data-testid="controllo-chart">
        <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Andamento mensile</h2>
        <p className="text-xs text-slate-500 mb-4">Ricavi, costi e utile per mese (contratti acquisiti + voci manuali con data).</p>
        {chartData.length === 0 ? (
          <div className="h-40 flex items-center justify-center text-sm text-slate-400">Nessun dato datato da mostrare.</div>
        ) : (
          <div style={{ width: "100%", height: 320 }}>
            <ResponsiveContainer>
              <ComposedChart data={chartData} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} width={36} />
                <RTooltip
                  formatter={(v, n) => [eur2(v), n]}
                  contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 12, fontSize: 12 }}
                  labelStyle={{ color: "hsl(var(--foreground))", fontWeight: 600 }}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="ricavi" name="Ricavi" fill="#10b981" radius={[4, 4, 0, 0]} maxBarSize={36} />
                <Bar dataKey="costi" name="Costi" fill="#ef4444" radius={[4, 4, 0, 0]} maxBarSize={36} />
                <Line dataKey="utile" name="Utile" stroke="#ea580c" strokeWidth={2.5} dot={{ r: 3 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        )}
      </Card>

      {/* Movimenti manuali */}
      <Card className="p-6 animate-fade-up">
        <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">Voci manuali di costo/ricavo</h2>
        <div className="space-y-2">
          {sortedMov.length === 0 && <p className="text-sm text-slate-400">Nessuna voce inserita.</p>}
          {sortedMov.map((m) => (
            <div key={m.id} data-testid={`movimento-row-${m.id}`} className="flex items-center gap-3 rounded-lg border border-slate-200 dark:border-slate-800 p-3">
              <span className={`h-8 w-8 rounded-lg flex items-center justify-center flex-shrink-0 ${m.tipo === "ricavo" ? "bg-emerald-100 text-emerald-600 dark:bg-emerald-500/15" : "bg-red-100 text-red-500 dark:bg-red-500/15"}`}>
                {m.tipo === "ricavo" ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />}
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-slate-800 dark:text-slate-100 truncate">{m.descrizione}</div>
                <div className="flex items-center gap-2 mt-0.5">
                  <Badge variant="outline" className="text-[10px] h-5">{areaLabel(m.area)}</Badge>
                  <span className="text-[11px] text-slate-400">{m.categoria} · {dateIt(m.data)}</span>
                </div>
              </div>
              <span className={`text-sm font-bold tabular-nums ${m.tipo === "ricavo" ? "text-emerald-600" : "text-red-500"}`}>{m.tipo === "ricavo" ? "+" : "−"}{eur2(m.importo)}</span>
              <button onClick={() => { setEditing(m); setForm({ ...emptyMov, ...m }); setOpen(true); }} className="text-slate-300 hover:text-slate-700 dark:hover:text-white"><Pencil className="h-3.5 w-3.5" /></button>
              <button onClick={() => del(m)} className="text-slate-300 hover:text-destructive"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          ))}
        </div>
      </Card>

      {/* PIN */}
      <Card className="p-6 animate-fade-up">
        <div className="flex items-center gap-2 mb-1"><KeyRound className="h-5 w-5 text-primary" /><h2 className="text-lg font-semibold text-slate-900 dark:text-white">PIN di accesso</h2></div>
        <p className="text-xs text-slate-500 mb-4">Cambia il PIN di questa pagina.</p>
        <div className="flex items-end gap-3 max-w-sm">
          <div className="grid gap-1.5 flex-1">
            <Label>Nuovo PIN</Label>
            <Input data-testid="controllo-new-pin-input" type="text" value={newPin} onChange={(e) => setNewPin(e.target.value)} placeholder="es. 4821" />
          </div>
          <Button data-testid="controllo-save-pin-button" onClick={savePin} variant="outline" className="rounded-full">Aggiorna</Button>
        </div>
      </Card>

      {/* Movimento dialog */}
      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(emptyMov); } }}>
        <DialogContent className="max-w-md" data-testid="movimento-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Modifica voce" : "Nuova voce"}</DialogTitle>
            <DialogDescription>Costo o ricavo manuale per il controllo economico.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-1">
            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-1.5">
                <Label>Tipo</Label>
                <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                  <SelectTrigger data-testid="movimento-tipo-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="costo">Costo</SelectItem><SelectItem value="ricavo">Ricavo</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="grid gap-1.5">
                <Label>Area</Label>
                <Select value={form.area} onValueChange={(v) => setForm({ ...form, area: v })}>
                  <SelectTrigger data-testid="movimento-area-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{MOVIMENTO_AREE.map((a) => <SelectItem key={a.key} value={a.key}>{a.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid gap-1.5"><Label>Descrizione *</Label><Input data-testid="movimento-descrizione-input" value={form.descrizione} onChange={(e) => setForm({ ...form, descrizione: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-1.5"><Label>Categoria</Label><Input value={form.categoria} onChange={(e) => setForm({ ...form, categoria: e.target.value })} /></div>
              <div className="grid gap-1.5"><Label>Importo (€) *</Label><Input data-testid="movimento-importo-input" type="number" value={form.importo} onChange={(e) => setForm({ ...form, importo: e.target.value })} /></div>
            </div>
            <div className="grid gap-1.5"><Label>Data *</Label><Input data-testid="movimento-data-input" type="date" value={form.data} onChange={(e) => setForm({ ...form, data: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button data-testid="movimento-submit-button" onClick={save}>Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
